import { useState } from 'react';
import { useSuspenseQuery } from '@tanstack/react-query';
import * as Combobox from '@givebutter/primitives/combobox';
import { LoadingOverlay } from '@givebutter/primitives/loading-overlay';
import { Separator } from '@givebutter/primitives/separator';
import { faUserGroup } from '@fortawesome/pro-regular-svg-icons';
import { fetchTeams } from 'api/teams';
import { FilterComboboxItems } from 'components/filter/filters-next/FilterComboboxItems';
import { FilterContainer } from 'components/filter/filters-next/FilterContainer';
import { FilterForm } from 'components/filter/filters-next/FilterForm';
import { FilterFormContext } from 'components/filter/filters-next/FilterFormContext';
import { FilterSubmit } from 'components/filter/filters-next/FilterSubmit';
import { ReadableFilterValue } from 'components/filter/filters-next/ReadableFilterValue';
import { transformMultiselectToReadable } from 'components/filter/filters-next/readable';
import { useCurrentFilter } from 'components/filter/filters-next/useCurrentFilter';
import { useFilter } from 'components/filter/filters-next/useFilter';
import { injectEquals } from 'components/filter/filters-next/util';
import {
    ContentFilterComponent,
    DownstreamFilterTransform,
    FilterFormValue,
    FilterIcon,
    FilterValue,
    LabelFilterComponent,
    ReadableFilterComponent,
    ServerKey,
    UpstreamFilterTransform,
} from 'components/filter/types';
import * as Form from 'components/form';
import { ValidationTextMap } from 'components/form/validations';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteTeams } from 'customHooks/useInfiniteTeams';
import { useIsPending } from 'customHooks/useIsPending';
import { identity } from 'lodash';
import { accountQueryKeys } from 'queries/account-query-keys';
import { Team } from 'types/domain';
import { PaginatedData } from 'types/pagination';

interface TeamsFilterFormValues extends FilterFormValue<string[]> {}

export const filterKey: ServerKey = 'teams';
export const Label: LabelFilterComponent = () => 'Team';

export const Icon: FilterIcon = faUserGroup;

export const Filter: ContentFilterComponent = () => {
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search);
    const { data, ref, isFetching } = useInfiniteTeams({ query: debouncedSearch });
    const teams = data?.pages ?? [];
    const { isPending, data: dTeams } = useSavedTeams();

    const filterContext = useFilter<number[], string[]>({
        formValueToFilter: team => team.map(t => parseInt(t)),
    });

    const methods = Form.useForm<TeamsFilterFormValues>({
        defaultValues: {
            value: dTeams.data.map(t => t.id.toString()),
        },
    });
    const { control, path } = methods;

    return (
        <LoadingOverlay size={'sm'} isLoading={isPending} delay={2000}>
            <FilterFormContext.Provider value={filterContext}>
                <Form.Provider {...methods}>
                    <FilterForm>
                        <FilterContainer>
                            <Form.Container>
                                <Form.Label name={path('value')}>Teams</Form.Label>
                                <Form.Combobox
                                    name="value"
                                    control={control}
                                    rules={{ required: ValidationTextMap.Required }}
                                >
                                    <Combobox.Content>
                                        <Combobox.Search
                                            placeholder="Search"
                                            value={search}
                                            onChangeValue={setSearch}
                                            isLoading={isFetching}
                                        />
                                        <FilterComboboxItems>
                                            {dTeams.data.map(item => (
                                                <Combobox.Item
                                                    key={item.id}
                                                    value={item.id.toString()}
                                                >
                                                    <Combobox.CheckboxIndicator />
                                                    <Combobox.ItemText>
                                                        {item.name}
                                                    </Combobox.ItemText>
                                                </Combobox.Item>
                                            ))}
                                            {dTeams.data.length > 0 && (
                                                <Separator className={'my-1'} />
                                            )}
                                            {teams.map(item => {
                                                if (dTeams.data.find(c => c.id === item.id)) {
                                                    return null;
                                                }

                                                return (
                                                    <Combobox.Item
                                                        key={item.id}
                                                        value={item.id.toString()}
                                                    >
                                                        <Combobox.CheckboxIndicator />
                                                        <Combobox.ItemText>
                                                            {item.name}
                                                        </Combobox.ItemText>
                                                    </Combobox.Item>
                                                );
                                            })}
                                            {teams.length === 0 && (
                                                <Combobox.NoneFoundText>
                                                    No teams found
                                                </Combobox.NoneFoundText>
                                            )}
                                            <div ref={ref} />
                                        </FilterComboboxItems>
                                    </Combobox.Content>
                                </Form.Combobox>
                                <Form.Error name={path('value')} />
                            </Form.Container>
                            <FilterSubmit />
                        </FilterContainer>
                    </FilterForm>
                </Form.Provider>
            </FilterFormContext.Provider>
        </LoadingOverlay>
    );
};
export const Readable: ReadableFilterComponent = () => {
    const { isPending, data: teams } = useSavedTeams();

    const valueStr = transformMultiselectToReadable(teams.data, {
        transform: t => t.name,
    });

    return (
        <LoadingOverlay isLoading={isPending} size={'sm'} delay={5000}>
            <ReadableFilterValue>{valueStr}</ReadableFilterValue>
        </LoadingOverlay>
    );
};

const useSavedTeams = () => {
    const { accountId } = useCurrentAccount();
    const currentFilter = useCurrentFilter<(string | number)[]>();
    const nextValue = currentFilter?.value.map(Number) ?? [];
    const [isPending, params] = useIsPending({
        ids: nextValue,
        page: 1,
        per_page: nextValue.length,
    });

    const query = useSuspenseQuery({
        queryKey: accountQueryKeys.teamsWithFilters(accountId, params),
        queryFn: () => {
            if (nextValue.length === 0) {
                return Promise.resolve({
                    data: [],
                    links: {} as any,
                    meta: {} as any,
                } as PaginatedData<Team>);
            }

            return fetchTeams(accountId, params);
        },
    });

    return { ...query, isPending };
};

export const downstream: DownstreamFilterTransform<Team> = identity;

export const upstream: UpstreamFilterTransform<Team> = (filter: FilterValue) =>
    injectEquals({
        value: filter.value,
    });
